import { currentCurve, type CurrentCurve } from './sources/currents';
import {
  EBB_COLOR,
  FLOOD_COLOR,
  renderCurrentChart,
  renderCurrentTable,
  streamAt,
} from './currentChart';
import { cardinal } from './types';
import type { CurrentStation } from './currentStations';

const timeFmt = new Intl.DateTimeFormat([], {
  weekday: 'short', hour: 'numeric', minute: '2-digit',
});

/**
 * Detail panel for a tidal current station.
 *
 * The arrow at the top is the point of the whole view: it points the way the
 * stream sets, and follows the cursor across the chart, so scrubbing the next
 * 33 hours shows the stream swinging through each turn rather than making the
 * reader decode a signed number.
 */
export function buildCurrentPopup(
  station: CurrentStation,
  onClose: () => void,
  /**
   * The moment the cursor is over, or null when it leaves. The caller mirrors
   * this onto the map; it is given a time rather than a stream so both sides
   * derive the bearing from the same source.
   */
  onScrub: (at: Date | null) => void = () => {},
): HTMLElement {
  const root = document.createElement('div');
  root.className = 'popup';

  root.innerHTML = `
    <header class="popup-head">
      <button type="button" class="popup-close" aria-label="Close station detail">×</button>
      <h2>${station.name}</h2>
      <p class="popup-sub">
        <span class="badge badge-current">Current station</span>
        <span class="muted">predicted, not measured</span>
      </p>
    </header>
    <div class="stream-now">
      <div class="stream-dial">
        <svg viewBox="-26 -26 52 52" width="72" height="72" aria-hidden="true">
          <circle class="dial-ring" cx="0" cy="0" r="23" />
          <text class="dial-n" x="0" y="-16" text-anchor="middle">N</text>
          <path class="dial-arrow" d="M0,-15 L7,10 L0,5 L-7,10 Z" />
          <circle class="dial-slack" cx="0" cy="0" r="4" />
        </svg>
      </div>
      <dl class="stream-meta">
        <div><dt>Stream</dt><dd class="stream-speed">—</dd></div>
        <div><dt>Sets</dt><dd class="stream-set">—</dd></div>
        <div><dt>At</dt><dd class="stream-time">—</dd></div>
      </dl>
    </div>
    <div class="chart-head">
      <span class="chart-range">Next 33 hours · knots</span>
      <div class="view-toggle" role="group" aria-label="Chart or table view">
        <button type="button" data-view="chart" aria-pressed="true">Chart</button>
        <button type="button" data-view="table" aria-pressed="false">Table</button>
      </div>
    </div>
    <div class="chart-host"><p class="chart-empty">Loading current prediction…</p></div>
    <a class="source-link"
       href="https://tidesandcurrents.noaa.gov/noaacurrents/predictions?id=${station.id}_1"
       target="_blank" rel="noopener">NOAA current predictions ↗</a>`;

  root.querySelector<HTMLButtonElement>('.popup-close')!.addEventListener('click', onClose);

  const host = root.querySelector<HTMLElement>('.chart-host')!;
  const buttons = root.querySelectorAll<HTMLButtonElement>('.view-toggle button');
  const arrow = root.querySelector<SVGPathElement>('.dial-arrow')!;
  const slackDot = root.querySelector<SVGCircleElement>('.dial-slack')!;
  const speedEl = root.querySelector<HTMLElement>('.stream-speed')!;
  const setEl = root.querySelector<HTMLElement>('.stream-set')!;
  const timeEl = root.querySelector<HTMLElement>('.stream-time')!;

  let curve: CurrentCurve | null = null;
  let view: 'chart' | 'table' = 'chart';

  /** Drives the dial, and the badge on the map marker with it. */
  const showAt = (at: Date | null) => {
    if (!curve) return;
    const when = at ?? new Date();
    const s = streamAt(curve, when);
    if (!s) return;

    // The map badge is shown only while scrubbing; letting go returns the map to
    // its resting state rather than leaving a stale bearing pinned to it.
    onScrub(at);

    const slack = s.phase === 'slack';
    arrow.style.opacity = slack ? '0' : '1';
    slackDot.style.opacity = slack ? '1' : '0';

    if (!slack && s.setDeg !== null) {
      // The dial arrow points the way the water goes, unlike the wind arrows
      // whose bearings are reported as the direction they come FROM.
      arrow.style.transform = `rotate(${s.setDeg}deg)`;
      arrow.setAttribute('fill', s.phase === 'ebb' ? EBB_COLOR : FLOOD_COLOR);
    }

    speedEl.textContent = slack
      ? 'slack'
      : `${s.speedKt.toFixed(1)} kt ${s.phase}`;
    setEl.innerHTML = s.setDeg === null
      ? '—'
      : `${cardinal(s.setDeg)} <span class="deg">${Math.round(s.setDeg)}°</span>`;
    timeEl.innerHTML = at
      ? timeFmt.format(when)
      : `<span class="muted">now</span>`;
  };

  const draw = () => {
    if (!curve) return;
    if (view === 'chart') renderCurrentChart(host, curve, showAt);
    else renderCurrentTable(host, curve);
    showAt(null);
  };

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      view = btn.dataset.view as 'chart' | 'table';
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
      draw();
    });
  });

  currentCurve(station.id)
    .then((loaded) => {
      curve = loaded;
      draw();
    })
    .catch((err) => {
      host.innerHTML = `<p class="chart-empty">Could not load current prediction: ${
        err instanceof Error ? err.message : String(err)
      }</p>`;
    });

  return root;
}
